/**
 * SC-971 — les raccourcis du formulaire « client au téléphone ».
 *
 * Tout ici est un RACCOURCI, jamais une règle : le champ libre reste la
 * source de vérité, et aucune de ces propositions n'empêche de saisir autre
 * chose. Le serveur a quelqu'un au bout du fil ; un formulaire qui discute
 * lui fait perdre la réservation.
 */

/** Les tailles de table qu'on propose d'un geste. Au-delà, le pas à pas. */
export const PARTY_CHIPS = [1, 2, 3, 4, 5, 6, 7, 8];

/** Plafond dur, le même que celui de hub (`validate_party_size`). */
export const MAX_PARTY_SIZE = 50;

/** Les heures de service qu'on propose d'un geste : le midi, puis le soir. */
const SERVICE_HOURS = [
  [11, 12, 13, 14],
  [18, 19, 20, 21, 22, 23],
];

/** Les minutes qu'on propose d'un geste. Toute autre minute se tape. */
export const MINUTE_CHIPS = [0, 15, 30, 45];

const HALF_HOUR_MS = 30 * 60_000;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `2026-09-12` pour la date locale du moment donné. */
export function toDateInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `19:30` pour l'heure locale du moment donné. */
export function toTimeInput(ms: number): string {
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** La prochaine demi-heure pleine, à partir de maintenant. */
export function nextHalfHour(nowMs: number): number {
  return Math.ceil(nowMs / HALF_HOUR_MS) * HALF_HOUR_MS;
}

/** Les minutes écoulées depuis minuit, à l'heure locale du moment donné. */
function minutesOfDay(ms: number): number {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * SC-1127 — les heures proposées en pastilles pour la date choisie, le midi
 * et le soir en deux groupes.
 *
 * Pour le jour qui court, seulement celles qui ont encore un quart d'heure
 * devant elles : un client qui appelle à 19h10 vient presque toujours ce
 * soir, lui montrer le midi ne fait qu'encombrer. Pour un autre jour, tout le
 * service — on ne sait pas encore quand il veut venir.
 */
export function quickHours(dateInput: string, nowMs: number): number[][] {
  if (dateInput !== toDateInput(nowMs)) return SERVICE_HOURS;
  const now = minutesOfDay(nowMs);
  const lastChip = MINUTE_CHIPS[MINUTE_CHIPS.length - 1];
  return SERVICE_HOURS.map((group) => group.filter((h) => h * 60 + lastChip >= now)).filter(
    (group) => group.length > 0,
  );
}

/** Vrai si ce moment de la date choisie est déjà derrière nous. */
export function isBehind(dateInput: string, hour: number, minute: number, nowMs: number): boolean {
  return dateInput === toDateInput(nowMs) && hour * 60 + minute < minutesOfDay(nowMs);
}

/**
 * L'heure que pose une pastille d'heure : l'heure pile, c'est ce qu'on
 * réserve le plus. Sauf pour l'heure qui court, où l'heure pile est déjà
 * passée — le premier quart d'heure encore à venir.
 */
export function timeAtHour(dateInput: string, hour: number, nowMs: number): string {
  const minute = MINUTE_CHIPS.find((m) => !isBehind(dateInput, hour, m, nowMs)) ?? 0;
  return `${pad(hour)}:${pad(minute)}`;
}

/**
 * Ce que le serveur tape dans le champ heure, chiffre après chiffre : `text`
 * est ce que le champ affiche (les deux points se posent seuls après deux
 * chiffres), `time` l'heure `HH:MM` dès qu'elle est complète, sinon vide.
 */
export function typedTime(raw: string): { text: string; time: string } {
  const split = raw.match(/^\s*(\d{1,2})\D+(\d{0,2})/);
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  const [h, m] = split ? [split[1], split[2]] : [digits.slice(0, 2), digits.slice(2)];
  const text = split || m ? `${h}:${m}` : h;
  const time = m.length === 2 && Number(h) < 24 && Number(m) < 60 ? `${h.padStart(2, '0')}:${m}` : '';
  return { text, time };
}

/**
 * La même saisie, quand le serveur quitte le champ sans l'avoir finie :
 * « 19 » vaut 19:00, « 19:3 » vaut 19:30, « 930 » vaut 09:30. Vide si ce
 * n'est pas une heure.
 */
export function settleTypedTime(raw: string): string {
  const { text, time } = typedTime(raw);
  if (time) return time;
  const [h, m = ''] = text.split(':');
  if (!h) return '';
  if (Number(h) < 24 && m.length <= 1 && Number(m) < 6) return `${h.padStart(2, '0')}:${m || '0'}0`;
  const late = h.slice(1) + m;
  if (late.length === 2 && Number(late) < 60) return `0${h[0]}:${late}`;
  return '';
}

/**
 * Le moment ISO que porte le corps de la requête, ou `null` si la date ou
 * l'heure manque. Construit dans le fuseau du téléphone, qui est celui du
 * restaurant : le serveur est dans la salle.
 */
export function toReservedForIso(dateInput: string, timeInput: string): string | null {
  const [y, m, d] = dateInput.split('-').map(Number);
  const [h, min] = timeInput.split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(h) || Number.isNaN(min)) return null;
  const at = new Date(y, m - 1, d, h, min, 0, 0);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

export interface NewReservationDraft {
  customerName: string;
  partySize: number;
  date: string;
  time: string;
  customerPhone: string;
  notes: string;
}

export function emptyDraft(nowMs: number): NewReservationDraft {
  const date = toDateInput(nowMs);
  // SC-1127 — la prochaine demi-heure n'est proposée que si elle tombe sur une
  // heure de service. À 10 h, « 10:30 » n'est pas un raccourci : c'est une
  // heure que personne n'a demandée et qui s'enregistrerait sans qu'aucune
  // pastille ne soit allumée. Mieux vaut un champ vide, que le serveur remplit.
  const next = nextHalfHour(nowMs);
  const offered = toDateInput(next) === date && quickHours(date, nowMs).flat().includes(new Date(next).getHours());
  return {
    customerName: '',
    partySize: 2,
    date,
    time: offered ? toTimeInput(next) : '',
    customerPhone: '',
    notes: '',
  };
}

/**
 * SC-1281 — le formulaire pré-rempli pour MODIFIER une réservation : ses
 * valeurs à elle, l'heure lue dans le fuseau du téléphone (celui du
 * restaurant, comme `toReservedForIso` qui la renverra).
 */
export function draftFromReservation(reservation: {
  customerName: string;
  partySize: number;
  reservedFor: string;
  customerPhone: string;
  notes: string;
}): NewReservationDraft {
  const at = Date.parse(reservation.reservedFor);
  return {
    customerName: reservation.customerName,
    partySize: reservation.partySize,
    date: toDateInput(at),
    time: toTimeInput(at),
    customerPhone: reservation.customerPhone,
    notes: reservation.notes,
  };
}

/**
 * Ce que hub exige : un nom et un nombre de personnes, plus un moment lisible.
 * Le téléphone et la note sont facultatifs — au téléphone on n'a souvent qu'un
 * prénom, et exiger un e-mail reviendrait à en faire inventer un.
 */
export function draftIsComplete(draft: NewReservationDraft): boolean {
  return (
    draft.customerName.trim().length > 0 &&
    draft.partySize >= 1 &&
    draft.partySize <= MAX_PARTY_SIZE &&
    toReservedForIso(draft.date, draft.time) !== null
  );
}
